// =============================================================================
// pages/Audit.js — Page dynamique /audit/:placeId (campagne prospection)
// =============================================================================
//
// Reprend l'audit Gemini (lib backend portée d'Adrien) en page complète, avec un
// seul CTA → /register?from=audit&place_id=XXX. Sert à doubler la conversion des
// cold-mails (CTA audit perso au lieu de /register générique).
//
// FR-only en V1 (pas de t('...')) : cohérent avec le moteur de prospection qui
// tape FR d'abord. L'i18n /audit fera l'objet d'une PR séparée post-merge.
//
// Données : GET /api/public/audit/:placeId (route publique, cache PostgreSQL 24h).
// SEO : noindex,nofollow (page perso par prospect) géré inline ci-dessous —
//        MetaHead n'est PAS utilisé car couplé i18n et sans support robots.
// Mise en page : feuille blanche (surface) flottant sur l'AuroraBackground, pour
//        un contenu lisible (texte sombre sur blanc) et cohérent avec le design
//        system (cards sur aurora). La page.tsx d'Adrien supposait un fond blanc.
// =============================================================================

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import { API_BASE } from '../config/api';
import '../styles/pg-audit.css';

// =============================================================================
// HELPERS
// =============================================================================

function formatRating(r) {
  return Number(r || 0).toFixed(1).replace('.', ',');
}

/**
 * Whitelist : ne garde QUE les balises <strong>…</strong>, échappe tout le reste.
 * Sécurise dangerouslySetInnerHTML contre une injection éventuelle de Gemini.
 * (Portée verbatim de page.tsx d'Adrien.)
 */
function sanitizeStrongOnly(html) {
  return String(html || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/&lt;strong&gt;/g, '<strong>')
    .replace(/&lt;\/strong&gt;/g, '</strong>');
}

/** Étoiles décoratives (pas une note interactive). Remplies selon la note. */
function Stars({ rating }) {
  const full = Math.floor(rating || 0);
  return (
    <span className="pg-audit-stars" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          className={`pg-audit-star ${i < full ? 'is-full' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.16c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118l-3.367-2.446a1 1 0 00-1.175 0l-3.367 2.446c-.785.57-1.84-.197-1.54-1.118l1.286-3.957a1 1 0 00-.364-1.118L2.46 9.384c-.783-.57-.38-1.81.588-1.81h4.16a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/**
 * SEO runtime : pose <meta robots="noindex,nofollow"> au mount et le titre,
 * retire le meta au unmount pour ne pas contaminer les autres routes du SPA.
 */
function useNoindexMeta(restaurant) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = restaurant ? `Audit Revyo — ${restaurant}` : 'Audit Revyo';

    let robots = document.head.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    const prevContent = robots.getAttribute('content');
    robots.setAttribute('content', 'noindex,nofollow');

    return () => {
      document.title = prevTitle;
      if (created) {
        robots.remove();
      } else if (prevContent != null) {
        robots.setAttribute('content', prevContent);
      }
    };
  }, [restaurant]);
}

// =============================================================================
// SOUS-COMPOSANTS D'ÉTAT
// =============================================================================

function AuditSkeleton() {
  return (
    <main className="pg-audit">
      <article className="pg-audit-sheet">
        <div className="pg-audit-photo pg-audit-shimmer" />
        <div className="pg-audit-content">
          <div className="pg-audit-sk-line pg-audit-sk-line--title pg-audit-shimmer" />
          <div className="pg-audit-sk-line pg-audit-sk-line--sub pg-audit-shimmer" />
          <div className="pg-audit-sk-line pg-audit-sk-line--note pg-audit-shimmer" />
          <div className="pg-audit-sk-block pg-audit-shimmer" />
          <div className="pg-audit-sk-block pg-audit-shimmer" />
        </div>
      </article>
    </main>
  );
}

function AuditError() {
  return (
    <main className="pg-audit">
      <div className="pg-audit-errorbox">
        <h1 className="pg-audit-error-title">Audit indisponible</h1>
        <p className="pg-audit-error-desc">
          Nous n'avons pas pu charger cet audit. Réessayez dans un instant ou
          retournez à l'accueil.
        </p>
        <Link to="/" className="pg-audit-error-cta">Retour à l'accueil</Link>
      </div>
    </main>
  );
}

// =============================================================================
// PAGE
// =============================================================================

export default function Audit() {
  const { placeId } = useParams();
  const [state, setState] = useState({ status: 'loading', audit: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', audit: null });
    fetch(`${API_BASE}/api/public/audit/${encodeURIComponent(placeId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((audit) => {
        if (!cancelled) setState({ status: 'ready', audit });
      })
      .catch((err) => {
        console.error('[Audit] fetch failed:', err);
        if (!cancelled) setState({ status: 'error', audit: null });
      });
    return () => {
      cancelled = true;
    };
  }, [placeId]);

  const { status, audit } = state;
  useNoindexMeta(audit && audit.restaurant);

  return (
    <>
      <AuroraBackground dim />
      <AuroraNavbar />

      {status === 'loading' && <AuditSkeleton />}
      {status === 'error' && <AuditError />}

      {status === 'ready' && audit && (
        <main className="pg-audit">
          <article className="pg-audit-sheet">
            {/* ---- Bandeau photo ---- */}
            {audit.photoUrl ? (
              <div className="pg-audit-photo">
                <img src={audit.photoUrl} alt={audit.restaurant} loading="eager" />
                <div className="pg-audit-photo-overlay" />
              </div>
            ) : (
              <div className="pg-audit-photo pg-audit-photo--fallback" />
            )}

            <div className="pg-audit-content">
              {/* ---- En-tête resto ---- */}
              <header className="pg-audit-head">
                <h1 className="pg-audit-title">{audit.restaurant}</h1>
                {audit.address && <p className="pg-audit-address">{audit.address}</p>}
                <div className="pg-audit-note-row">
                  <span className="pg-audit-note">{formatRating(audit.rating)}</span>
                  <Stars rating={audit.rating} />
                  <span className="pg-audit-note-sub">
                    sur {Number(audit.reviewCount || 0).toLocaleString('fr-FR')} avis Google
                  </span>
                </div>
              </header>

              {/* ---- Ce qui ressort de votre fiche (description Gemini) ---- */}
              <section className="pg-audit-block">
                <h2 className="pg-audit-eyebrow">Ce qui ressort de votre fiche</h2>
                <p
                  className="pg-audit-desc"
                  dangerouslySetInnerHTML={{ __html: sanitizeStrongOnly(audit.description) }}
                />
              </section>

              {/* ---- Le frein (constant V4) ---- */}
              {audit.frein && (
                <section className="pg-audit-block">
                  <div className="pg-audit-frein">
                    <p>{audit.frein}</p>
                  </div>
                </section>
              )}

              {/* ---- L'objectif (phrase 3 + phrase Revyo) ---- */}
              <section className="pg-audit-block">
                <h2 className="pg-audit-eyebrow">L'objectif</h2>
                <p className="pg-audit-phrase3">{audit.phrase3}</p>
                {audit.phraseRevyo && (
                  <p className="pg-audit-phrase-revyo">{audit.phraseRevyo}</p>
                )}
              </section>

              {/* ---- CTA principal ---- */}
              <section className="pg-audit-block pg-audit-block--cta">
                <div className="pg-audit-cta-card">
                  <p className="pg-audit-cta-intro">
                    Pour structurer la captation d'avis chez vous, l'essai est gratuit
                    14 jours, sans carte bancaire, sans engagement.
                  </p>
                  <a className="pg-audit-cta-btn" href={audit.ctaHref}>
                    {audit.ctaLabel || 'Créer mon compte gratuit (14 jours)'}
                  </a>
                  <p className="pg-audit-cta-sub">
                    Mise en route en 2 minutes. Vos premiers avis Google capturés dès
                    la semaine prochaine.
                  </p>
                </div>
              </section>
            </div>
          </article>
        </main>
      )}

      <Footer />
    </>
  );
}
